import React, { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAdminDashboard, useRecentActivity, useRecentComplaints } from '../../hooks/useDashboard';
import { useNavigation } from '../../context/NavigationContext';
import { KPICard } from '../../components/dashboard/KPICard';
import { ChartCard } from '../../components/dashboard/ChartCard';
import { FilterBar } from '../../components/dashboard/FilterBar';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { PeriodSelect } from '../../components/dashboard/PeriodSelect';
import { RecentActivityCard } from '../../components/dashboard/RecentActivityCard';
import { RecentComplaintsTable } from '../../components/dashboard/RecentComplaintsTable';
import { ComplaintTrendChart } from '../../components/dashboard/charts/ComplaintTrendChart';
import { CategoryPieChart } from '../../components/dashboard/charts/CategoryPieChart';
import { DepartmentBarChart } from '../../components/dashboard/charts/DepartmentBarChart';
import { SLAGaugeChart } from '../../components/dashboard/charts/SLAGaugeChart';
import { CostTrendChart } from '../../components/dashboard/charts/CostTrendChart';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Complaint } from '../../types/complaint';

interface AdminDashboardPageProps {
  onSelectComplaint?: (complaint: Complaint) => void;
}

export const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({ onSelectComplaint }) => {
  const { data, loading, error, filter, setFilter, refetch } = useAdminDashboard();
  const { data: activity, loading: activityLoading, error: activityError } = useRecentActivity(6);
  const { data: recentComplaints, loading: complaintsLoading, error: complaintsError } = useRecentComplaints(5);
  const { setActiveTab } = useNavigation();
  const [months, setMonths] = useState(6);

  const trend = useMemo(() => {
    if (!data) return [];
    return months === 6 ? data.complaint_trend.slice(-6) : data.complaint_trend;
  }, [data, months]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-rose-400 text-sm p-4">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <FilterBar
          filter={filter}
          onChange={setFilter}
          showDepartment
          showStatus
          showPriority
          departments={[
            { id: 'Production', name: 'Production' },
            { id: 'Maintenance', name: 'Maintenance' },
            { id: 'Facilities', name: 'Facilities' },
            { id: 'IT', name: 'IT' },
          ]}
        />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refetch}
            icon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh
          </Button>
          <ExportButton
            config={{
              filename: 'admin-dashboard',
              sheetName: 'Admin Dashboard',
              columns: [
                { key: 'label', header: 'Metric', width: 25 },
                { key: 'value', header: 'Value', width: 15 },
                { key: 'trend', header: 'Trend', width: 25 },
              ],
              data: data.kpis,
              filters: filter,
              generatedAt: new Date().toISOString(),
              generatedBy: 'Admin',
            }}
          />
        </div>
      </div>

      <DashboardGrid columns={4}>
        {data.kpis.map((kpi, i) => (
          <KPICard key={i} metric={kpi} />
        ))}
      </DashboardGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard
          title="Complaint Overview"
          subtitle="Monthly volume"
          className="lg:col-span-2"
          action={<PeriodSelect value={months} onChange={setMonths} />}
        >
          <ComplaintTrendChart data={trend} />
        </ChartCard>

        <RecentActivityCard
          entries={activity}
          loading={activityLoading}
          error={activityError}
        />
      </div>

      <RecentComplaintsTable
        complaints={recentComplaints}
        loading={complaintsLoading}
        error={complaintsError}
        onSelect={onSelectComplaint}
        onViewAll={() => setActiveTab('complaints')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Category Distribution" subtitle="Complaints by category">
          <CategoryPieChart data={data.category_distribution} />
        </ChartCard>

        <ChartCard title="Department Performance" subtitle="Complaints vs Resolved">
          <DepartmentBarChart data={data.department_performance} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="SLA Compliance" subtitle="By priority level">
          <SLAGaugeChart data={data.sla_compliance} />
        </ChartCard>

        <ChartCard title="Cost Trend" subtitle="Monthly expenditure breakdown">
          <CostTrendChart data={data.cost_trend} />
        </ChartCard>
      </div>
    </div>
  );
};
